import type { InternalEnrichedFinding } from '../../../../../types';
import type { FindingData } from '../types';
import type { EditableFields } from '../../../../findings/finding-editable-fields';

export const convertToEnrichedFinding = (
  finding: FindingData,
): InternalEnrichedFinding => {
  // Build assets array from endpointId and webpageId
  // EnrichedAsset requires: type, id, method, path, url, apiProtocol
  const assets: InternalEnrichedFinding['assets'] = [];
  const rawAssets: Array<{ type: 'ENDPOINT' | 'WEBPAGE'; id: string }> = [];

  if (finding.endpointId) {
    assets.push({
      type: 'ENDPOINT',
      id: finding.endpointId,
      method: null,
      path: null,
      url: null,
      graphqlOperationName: null,
      soapOperationName: null,
      apiProtocol: null,
    });
    rawAssets.push({ type: 'ENDPOINT', id: finding.endpointId });
  }

  if (finding.webpageId) {
    assets.push({
      type: 'WEBPAGE',
      id: finding.webpageId,
      method: null,
      path: null,
      url: null,
      graphqlOperationName: null,
      soapOperationName: null,
      apiProtocol: null,
    });
    rawAssets.push({ type: 'WEBPAGE', id: finding.webpageId });
  }

  return {
    id: `hitl-finding-${Date.now()}`,
    tenantId: '',
    targetId: '',
    targetType: 'WEB_APPLICATION',
    applicationId: '',
    executionStateId: null,
    traceThreadId: finding.traceThreadId || null,
    signalId: finding.signalId || null,
    pocBlocks: finding.pocBlocks ?? null,
    // Note: We don't populate signal object since we only have signalId
    // The FindingEditDrawer will fetch signal details via onSearchSignals
    status:
      (finding.status as InternalEnrichedFinding['status']) || 'Unresolved',
    severity: 'Low',
    category: (finding.category as InternalEnrichedFinding['category']) || null,
    cweIds: [],
    owaspCategory: null,
    title: finding.title || 'Untitled Finding',
    shortDescription: finding.shortDescription ?? null,
    assignedTo: '',
    discoveryDate: new Date(),
    publishedAt: null,
    resolvedAt: null,
    createdAt: new Date(),
    updatedAt: new Date(),
    filePath: null,
    metadata: null,
    cvss: null,
    assets,
    rawAssets,
    externalReferences: [],
    description: finding.description || 'No description provided',
    poc: finding.poc || 'No POC provided',
    businessImpact: finding.businessImpact || 'No impact specified',
    attackCapabilities: null,
    mitigationPlan: finding.mitigationPlan || 'No mitigation specified',
    findingReport: null,
    exploitationGuide: finding.exploitationGuide || null,
    executionScript: finding.executionScript || null,
    pocScript: null,
    pocScriptBlockedByGuardrails: null,
    pocScriptGuardrailsFeedback: null,
    remediationPlan: null,
    remediationPlanGeneratedAt: null,
    remediationPr: null,
    findingUpdateComment: null,
    hasRetests: false,
    retestThreadIds: [],
    constituentFindingIds: [],
    pendingProposedSeverity: null,
    owaspRisk: null,
    isPublished: false,
  };
};

export const convertToFindingToolInput = ({
  original,
  updates,
  originalArgs,
}: {
  original: InternalEnrichedFinding;
  updates: Partial<EditableFields>;
  originalArgs?: Record<string, unknown>;
}): Record<string, unknown> => {
  const title = updates.title ?? original.title ?? 'Untitled Finding';
  const shortDescription =
    updates.shortDescription ??
    original.shortDescription ??
    'No short description provided';
  const description =
    updates.description ?? original.description ?? 'No description provided';
  const poc = updates.poc ?? original.poc ?? 'No POC provided';
  const businessImpact =
    updates.businessImpact ?? original.businessImpact ?? 'No impact specified';
  const mitigationPlan =
    updates.mitigationPlan ??
    original.mitigationPlan ??
    'No mitigation specified';

  const exploitationGuide =
    original.exploitationGuide ||
    (originalArgs?.exploitationGuide as string) ||
    undefined;
  const executionScript =
    original.executionScript ||
    (originalArgs?.executionScript as string) ||
    undefined;
  const findingNote = (originalArgs?.findingNote as string) || undefined;

  // Get linking fields from updates (EditableFields) or originalArgs
  const signalId =
    updates.signalId ?? original.signalId ?? (originalArgs?.signalId as string);
  const traceThreadId =
    updates.traceThreadId ??
    original.traceThreadId ??
    (originalArgs?.traceThreadId as string);
  const postAttackTraceId = (
    originalArgs?.postAttackTraceId as string | undefined
  )?.trim();

  // Extract asset IDs from updates or original
  const assets = updates.assets ?? original.rawAssets ?? [];
  const endpointId =
    assets.find((asset) => asset.type === 'ENDPOINT')?.id ??
    (originalArgs?.endpointId as string);
  const webpageId =
    assets.find((asset) => asset.type === 'WEBPAGE')?.id ??
    (originalArgs?.webpageId as string);

  const pocBlocks =
    updates.pocBlocks !== undefined
      ? updates.pocBlocks
      : (original.pocBlocks ?? null);

  const result: Record<string, unknown> = {
    title: String(title),
    shortDescription: String(shortDescription),
    description: String(description),
    poc: String(poc),
    businessImpact: String(businessImpact),
    mitigationPlan: String(mitigationPlan),
  };

  if (pocBlocks !== null || updates.pocBlocks !== undefined) {
    result.pocBlocks = pocBlocks;
  }

  if (updates.category !== undefined) {
    result.category = updates.category;
  }

  if (updates.status !== undefined) {
    result.status = updates.status;
  }

  if (signalId) {
    result.signalId = signalId;
  }

  if (traceThreadId) {
    result.traceThreadId = traceThreadId;
  }

  if (postAttackTraceId) {
    result.postAttackTraceId = postAttackTraceId;
  }

  if (endpointId) {
    result.endpointId = endpointId;
  }

  if (webpageId) {
    result.webpageId = webpageId;
  }

  if (exploitationGuide) {
    result.exploitationGuide = exploitationGuide;
  }

  if (executionScript) {
    result.executionScript = executionScript;
  }

  if (findingNote) {
    result.findingNote = findingNote;
  }

  return result;
};
